/**
 * Behaviour for the Jaguar Furniture sections (jf-*). Each feature is a custom element,
 * so it re-initialises cleanly when the theme editor re-renders a section.
 */
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** Floating header: denser glass after scrolling, mobile menu toggle, live cart count. */
class JfHeader extends HTMLElement {
  connectedCallback() {
    this.controller = new AbortController();
    const { signal } = this.controller;

    // Horizon scrolls `.page-wrapper` rather than the window, so listen for either.
    const scroller = document.querySelector('.page-wrapper');
    const updateScrolled = () =>
      this.classList.toggle('is-scrolled', window.scrollY > 0 || (scroller?.scrollTop ?? 0) > 0);
    updateScrolled();
    window.addEventListener('scroll', updateScrolled, { passive: true, signal });
    scroller?.addEventListener('scroll', updateScrolled, { passive: true, signal });

    const toggle = this.querySelector('[data-menu-toggle]');
    const menu = this.querySelector('[data-menu]');
    if (toggle && menu) {
      const setOpen = (open) => {
        toggle.setAttribute('aria-expanded', String(open));
        toggle.setAttribute('aria-label', open ? toggle.dataset.labelClose : toggle.dataset.labelOpen);
        menu.hidden = !open;
        this.classList.toggle('is-menu-open', open);
      };
      toggle.addEventListener('click', () => setOpen(toggle.getAttribute('aria-expanded') !== 'true'), { signal });
      document.addEventListener(
        'keydown',
        (event) => {
          if (event.key === 'Escape') setOpen(false);
        },
        { signal }
      );
    }

    const accountToggle = this.querySelector('[data-account-toggle]');
    const accountOverlay = this.querySelector('[data-account-overlay]');
    const accountPanel = this.querySelector('[data-account-panel]');
    if (accountToggle && accountOverlay && accountPanel) {
      const setAccountOpen = (open, { returnFocus = false } = {}) => {
        accountToggle.setAttribute('aria-expanded', String(open));
        accountOverlay.hidden = !open;
        if (open) accountPanel.querySelector('[data-account-close]')?.focus();
        else if (returnFocus) accountToggle.focus();
      };
      accountToggle.addEventListener('click', () => setAccountOpen(accountOverlay.hidden), { signal });
      accountPanel
        .querySelector('[data-account-close]')
        ?.addEventListener('click', () => setAccountOpen(false, { returnFocus: true }), { signal });
      accountOverlay.addEventListener(
        'click',
        (event) => {
          if (event.target === accountOverlay) setAccountOpen(false, { returnFocus: true });
        },
        { signal }
      );
      document.addEventListener(
        'keydown',
        (event) => {
          if (event.key === 'Escape' && !accountOverlay.hidden) setAccountOpen(false, { returnFocus: true });
        },
        { signal }
      );

      // Shopify's sign-in reads the consent from the URL, so both sign-in routes carry the checkbox state.
      accountPanel.querySelector('[data-account-consent]')?.addEventListener(
        'change',
        (event) => {
          const consent = String(event.target instanceof HTMLInputElement && event.target.checked);
          const field = accountPanel.querySelector('[data-account-consent-field]');
          if (field instanceof HTMLInputElement) field.value = consent;
          accountPanel.querySelectorAll('[data-account-consent-link]').forEach((link) => {
            if (!(link instanceof HTMLAnchorElement)) return;
            const url = new URL(link.href);
            url.searchParams.set('marketing_consent', consent);
            link.href = url.toString();
          });
        },
        { signal }
      );
    }

    const count = this.querySelector('[data-cart-count]');
    if (count) {
      document.addEventListener(
        StandardEvents.cartLinesUpdate,
        (event) => {
          event.promise
            ?.then(({ cart, detail }) => {
              const itemCount = cart?.totalQuantity ?? detail?.itemCount ?? 0;
              count.textContent = String(itemCount);
              count.hidden = itemCount === 0;
            })
            .catch(() => {});
        },
        { signal }
      );
    }
  }

  disconnectedCallback() {
    this.controller?.abort();
  }
}

/** Adds `is-revealed` the first time the element scrolls into view. */
class JfReveal extends HTMLElement {
  connectedCallback() {
    if (prefersReducedMotion() || !('IntersectionObserver' in window)) {
      this.classList.add('is-revealed');
      return;
    }
    this.observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          this.classList.add('is-revealed');
          this.observer?.disconnect();
        }
      },
      { threshold: 0.12 }
    );
    this.observer.observe(this);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }
}

/** Streams and plays the wrapped video only while it is on screen. */
class JfAutoplayVideo extends HTMLElement {
  connectedCallback() {
    const video = this.querySelector('video');
    if (!video) return;
    if (prefersReducedMotion()) {
      video.preload = 'metadata';
      return;
    }
    this.observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          video.preload = 'auto';
          video.play().catch(() => {});
        } else {
          video.pause();
        }
      },
      { threshold: 0.25 }
    );
    this.observer.observe(video);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }
}

/** Swipeable image slides with previous/next arrows on desktop. */
class JfSlideshow extends HTMLElement {
  connectedCallback() {
    this.controller = new AbortController();
    const { signal } = this.controller;
    const slides = this.querySelector('[data-slides]');
    if (!slides) return;

    const items = Array.from(slides.children);
    const activeIndex = () => Math.round(slides.scrollLeft / Math.max(slides.clientWidth, 1));
    const syncHidden = () => {
      const active = activeIndex();
      items.forEach((item, index) => item.toggleAttribute('aria-hidden', index !== active));
    };
    syncHidden();
    slides.addEventListener('scroll', syncHidden, { passive: true, signal });

    this.querySelectorAll('[data-step]').forEach((button) => {
      button.addEventListener(
        'click',
        () => {
          const step = Number(button.getAttribute('data-step'));
          const next = (activeIndex() + step + items.length) % items.length;
          slides.scrollTo({ left: next * slides.clientWidth });
        },
        { signal }
      );
    });

    if (this.hasAttribute('hover-preview') && items.length > 1) {
      items[1].querySelector('img[loading="lazy"]')?.removeAttribute('loading');
      const jumpTo = (index) => slides.scrollTo({ left: index * slides.clientWidth, behavior: 'instant' });
      let restoreIndex = null;
      // Overlays such as the quick add button sit beside the slideshow, so hovering them must keep the preview.
      const hoverArea = this.parentElement ?? this;
      hoverArea.addEventListener(
        'pointerenter',
        (event) => {
          if (event.pointerType !== 'mouse' || activeIndex() !== 0) return;
          restoreIndex = 0;
          jumpTo(1);
        },
        { signal }
      );
      hoverArea.addEventListener(
        'pointerleave',
        (event) => {
          if (event.pointerType !== 'mouse' || restoreIndex === null) return;
          jumpTo(restoreIndex);
          restoreIndex = null;
        },
        { signal }
      );
    }
  }

  disconnectedCallback() {
    this.controller?.abort();
  }
}

/** Horizontal product carousel; arrows appear only when there is more to scroll to. */
class JfCarousel extends HTMLElement {
  connectedCallback() {
    this.controller = new AbortController();
    const { signal } = this.controller;
    const track = this.querySelector('[data-track]');
    const previous = this.querySelector('[data-previous]');
    const next = this.querySelector('[data-next]');
    if (!track) return;

    const update = () => {
      const maxScroll = track.scrollWidth - track.clientWidth;
      if (previous) previous.hidden = track.scrollLeft <= 1;
      if (next) next.hidden = track.scrollLeft >= maxScroll - 1;
    };
    update();
    track.addEventListener('scroll', update, { passive: true, signal });
    window.addEventListener('resize', update, { signal });

    const scrollByPage = (direction) => track.scrollBy({ left: direction * track.clientWidth * 0.8 });
    previous?.addEventListener('click', () => scrollByPage(-1), { signal });
    next?.addEventListener('click', () => scrollByPage(1), { signal });
  }

  disconnectedCallback() {
    this.controller?.abort();
  }
}

const AUTO_SCROLL_PX_PER_FRAME = 0.5;
/** Pointer travel beyond which a press is treated as a drag rather than a click. */
const DRAG_CLICK_THRESHOLD_PX = 6;

/**
 * Instagram reel strip: endless auto-scroll (the track holds the reels twice and wraps at half
 * its width), mouse drag, play-only-what-is-visible and lazy video loading.
 */
class JfReelStrip extends HTMLElement {
  connectedCallback() {
    this.controller = new AbortController();
    const { signal } = this.controller;
    const strip = this.querySelector('[data-strip]');
    const track = strip?.firstElementChild;
    if (!strip || !(track instanceof HTMLElement)) return;

    const videos = Array.from(strip.querySelectorAll('video'));
    const connection = navigator.connection;
    const reduceMotion = prefersReducedMotion();
    const autoplayAllowed = connection?.saveData !== true && !reduceMotion;

    const videosInView = new WeakSet();
    let stripVisible = true;
    let hovering = false;
    let dragging = false;
    let dragMoved = false;
    let loopWidth = 0;
    let position = 0;

    const measure = () => {
      loopWidth = track.scrollWidth / 2;
    };
    const wrapIntoLoop = (value) => (loopWidth > 0 ? ((value % loopWidth) + loopWidth) % loopWidth : value);
    const play = (video) => {
      if (video.preload !== 'auto') video.preload = 'auto';
      video.play().catch(() => {});
    };

    const preloadObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const video = entry.target;
          if (entry.isIntersecting && video.preload === 'none') video.preload = 'metadata';
        }
      },
      { rootMargin: '150px' }
    );

    const playbackObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const video = entry.target;
          if (entry.isIntersecting) {
            videosInView.add(video);
            if (stripVisible && autoplayAllowed) play(video);
          } else {
            videosInView.delete(video);
            video.pause();
          }
        }
      },
      { root: strip, threshold: 0.6 }
    );

    const stripObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          stripVisible = entry.isIntersecting;
          if (stripVisible) {
            measure();
            if (autoplayAllowed) videos.filter((video) => videosInView.has(video)).forEach(play);
          } else {
            videos.forEach((video) => video.pause());
          }
        }
      },
      { threshold: 0.05 }
    );

    for (const video of videos) {
      preloadObserver.observe(video);
      playbackObserver.observe(video);
      const reel = video.parentElement;
      reel?.addEventListener('mouseenter', () => play(video), { signal });
      reel?.addEventListener(
        'mouseleave',
        () => {
          if (!videosInView.has(video)) video.pause();
        },
        { signal }
      );
    }
    stripObserver.observe(strip);
    signal.addEventListener('abort', () => {
      preloadObserver.disconnect();
      playbackObserver.disconnect();
      stripObserver.disconnect();
    });

    measure();
    window.addEventListener('resize', measure, { signal });
    strip.addEventListener('mouseenter', () => (hovering = true), { signal });
    strip.addEventListener('mouseleave', () => (hovering = false), { signal });

    let frameId = 0;
    const tick = () => {
      if (Math.abs(strip.scrollLeft - position) > 1) position = strip.scrollLeft;
      if (stripVisible && !hovering && !dragging && !reduceMotion) position += AUTO_SCROLL_PX_PER_FRAME;
      position = wrapIntoLoop(position);
      strip.scrollLeft = position;
      frameId = requestAnimationFrame(tick);
    };
    frameId = requestAnimationFrame(tick);
    signal.addEventListener('abort', () => cancelAnimationFrame(frameId));

    let startX = 0;
    let startScroll = 0;
    let maxDragDistance = 0;

    strip.addEventListener(
      'pointerdown',
      (event) => {
        if (event.pointerType !== 'mouse') return;
        dragging = true;
        maxDragDistance = 0;
        startX = event.clientX;
        startScroll = strip.scrollLeft;
        strip.classList.add('is-dragging');
        strip.setPointerCapture(event.pointerId);
      },
      { signal }
    );

    strip.addEventListener(
      'pointermove',
      (event) => {
        if (!dragging) return;
        const dx = event.clientX - startX;
        maxDragDistance = Math.max(maxDragDistance, Math.abs(dx));
        position = wrapIntoLoop(startScroll - dx);
        strip.scrollLeft = position;
      },
      { signal }
    );

    const endDrag = () => {
      if (!dragging) return;
      dragging = false;
      strip.classList.remove('is-dragging');
      dragMoved = maxDragDistance > DRAG_CLICK_THRESHOLD_PX;
      setTimeout(() => (dragMoved = false), 60);
    };
    strip.addEventListener('pointerup', endDrag, { signal });
    strip.addEventListener('pointercancel', endDrag, { signal });

    strip.addEventListener(
      'click',
      (event) => {
        if (dragMoved) {
          event.preventDefault();
          event.stopPropagation();
        }
      },
      { capture: true, signal }
    );
  }

  disconnectedCallback() {
    this.controller?.abort();
  }
}

/** Opens the wrapped <dialog> from [data-dialog-open]; closes on [data-dialog-close] or a backdrop click. */
class JfDialog extends HTMLElement {
  connectedCallback() {
    this.controller = new AbortController();
    this.addEventListener(
      'click',
      (event) => {
        const dialog = this.querySelector('dialog');
        const target = event.target;
        if (!dialog || !(target instanceof Element)) return;

        if (target.closest('[data-dialog-open]')) {
          dialog.showModal();
          return;
        }
        if (target.closest('[data-dialog-close]')) {
          dialog.close();
          return;
        }
        if (target === dialog) {
          const rect = dialog.getBoundingClientRect();
          const inside =
            event.clientX >= rect.left &&
            event.clientX <= rect.right &&
            event.clientY >= rect.top &&
            event.clientY <= rect.bottom;
          if (!inside) dialog.close();
        }
      },
      { signal: this.controller.signal }
    );
  }

  disconnectedCallback() {
    this.controller?.abort();
  }
}

/** Share button: native share sheet on touch devices, otherwise a menu of share links. */
class JfShare extends HTMLElement {
  connectedCallback() {
    this.controller = new AbortController();
    const { signal } = this.controller;
    const toggle = this.querySelector('[data-share-toggle]');
    const menu = this.querySelector('[data-share-menu]');
    if (!toggle || !menu) return;

    const setOpen = (open) => {
      menu.hidden = !open;
      toggle.setAttribute('aria-expanded', String(open));
    };

    toggle.addEventListener(
      'click',
      async () => {
        const { url, title } = this.dataset;
        if (navigator.share && window.matchMedia('(pointer: coarse)').matches) {
          try {
            await navigator.share({ url, title });
            return;
          } catch (error) {
            if (error instanceof DOMException && error.name === 'AbortError') return;
          }
        }
        setOpen(menu.hidden);
      },
      { signal }
    );

    this.querySelector('[data-share-copy]')?.addEventListener(
      'click',
      async () => {
        const label = this.querySelector('[data-share-copy-label]');
        const status = this.querySelector('[data-share-status]');
        try {
          await navigator.clipboard.writeText(this.dataset.url ?? window.location.href);
          if (label) label.textContent = 'Link copied';
          if (status) status.textContent = 'Link copied';
          window.setTimeout(() => {
            if (label) label.textContent = 'Copy link';
            if (status) status.textContent = '';
          }, 2000);
        } catch {
          if (label) label.textContent = 'Copy failed';
        }
      },
      { signal }
    );

    document.addEventListener(
      'click',
      (event) => {
        if (!menu.hidden && event.target instanceof Node && !this.contains(event.target)) setOpen(false);
      },
      { signal }
    );
    document.addEventListener(
      'keydown',
      (event) => {
        if (event.key === 'Escape' && !menu.hidden) {
          setOpen(false);
          toggle.focus();
        }
      },
      { signal }
    );
  }

  disconnectedCallback() {
    this.controller?.abort();
  }
}

// Local theme preview only: open the zoom gallery and the cart drawer as normal (non-modal) dialogs. Modal dialogs
// render in the browser's top layer, above any in-page inspector overlay, so element pickers cannot reach their contents.
if (['127.0.0.1', 'localhost'].includes(window.location.hostname)) {
  const showModal = HTMLDialogElement.prototype.showModal;
  HTMLDialogElement.prototype.showModal = function () {
    const cartDrawer = this.closest('#cart-drawer');
    if (cartDrawer) {
      cartDrawer.classList.add('jf-inspectable-drawer');
      this.show();
      return;
    }
    if (!this.classList.contains('dialog-zoomed-gallery__dialog')) return showModal.call(this);
    this.classList.add('jf-inspectable-dialog');
    this.show();
    this.focus();
  };

  // Stands in for the modal backdrop the non-modal drawer lacks: it dims the page and closes the drawer when clicked.
  document.addEventListener('click', (event) => {
    const drawer = event.target instanceof Element && event.target.closest('#cart-drawer.jf-inspectable-drawer[open]');
    if (drawer && event.target === drawer && 'close' in drawer) drawer.close();
  });
}

// Product gallery: "Click to see full view" opens the zoom dialog at the slide on screen.
document.addEventListener('click', (event) => {
  if (!(event.target instanceof Element) || !event.target.closest('[data-full-view]')) return;
  const gallery = event.target.closest('media-gallery');
  const slideshow = gallery?.querySelector('slideshow-component');
  const slides = gallery?.querySelectorAll('slideshow-slide');
  const index = Number(slideshow?.current ?? 0);
  const slide = slides?.[index] ?? slides?.[0];
  if (slide instanceof HTMLElement) slide.click();
});

// Product gallery: hovering a thumbnail shows that image, as on Amazon. Touch keeps tap-to-select.
document.addEventListener('pointerover', (event) => {
  if (event.pointerType !== 'mouse' || !(event.target instanceof Element)) return;
  const thumbnail = event.target.closest('.media-gallery--carousel .slideshow-controls__thumbnail');
  if (thumbnail instanceof HTMLButtonElement && thumbnail.getAttribute('aria-selected') !== 'true') {
    thumbnail.click();
  }
});

// Quick add buttons keep a tick while their variant is in the cart; clicking the tick removes it from the cart.
const quickAddSelector = '.quick-add__button--add';
const cartUrl = () => window.Theme?.routes?.cart_url ?? '/cart';
/** @param {HTMLButtonElement} button */
const quickAddVariantId = (button) => button.form?.querySelector('input[name="id"]')?.value;

const fetchCart = () =>
  fetch(`${cartUrl()}.js`, { headers: { Accept: 'application/json' }, cache: 'no-store' }).then((response) =>
    response.json()
  );

/** @param {Array<{ variant_id: number }>} items */
const syncQuickAddButtons = (items) => {
  const variantIds = new Set(items.map((item) => String(item.variant_id)));
  document.querySelectorAll(quickAddSelector).forEach((button) => {
    if (!(button instanceof HTMLButtonElement)) return;
    const inCart = variantIds.has(quickAddVariantId(button) ?? '');
    button.toggleAttribute('data-in-cart', inCart);
    if (inCart && button.dataset.removeLabel) button.setAttribute('aria-label', button.dataset.removeLabel);
    else button.removeAttribute('aria-label');
  });
};

document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  event.promise
    ?.then(() => fetchCart())
    .then((cart) => syncQuickAddButtons(cart.items))
    .catch(() => {});
});

window.addEventListener('pageshow', (event) => {
  if (event.persisted) fetchCart().then((cart) => syncQuickAddButtons(cart.items)).catch(() => {});
});

/** @param {HTMLButtonElement} button */
const removeFromCart = async (button) => {
  const variantId = quickAddVariantId(button);
  if (!variantId || button.getAttribute('aria-busy') === 'true') return;
  button.setAttribute('aria-busy', 'true');

  try {
    const lines = (await fetchCart()).items.filter((item) => String(item.variant_id) === variantId);
    if (lines.length === 0) {
      button.removeAttribute('data-in-cart');
      return;
    }

    const deferred = CartLinesUpdateEvent.createPromise();
    button.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'remove',
        context: 'product',
        lines: lines.map((line) => ({ id: line.key, quantity: 0 })),
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(`${cartUrl()}/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates: Object.fromEntries(lines.map((line) => [line.key, 0])) }),
      });
      const cart = await response.json();
      if (!response.ok) throw new Error(cart.description ?? cart.message ?? 'Remove from cart failed');

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { items: cart.items, itemCount: cart.item_count, source: 'jf-quick-add', didError: false },
      });
    } catch (error) {
      deferred.reject(error);
    }
  } finally {
    button.removeAttribute('aria-busy');
  }
};

// Capture on window so this runs before Horizon's document-level `on:click` handlers and the form submit.
window.addEventListener(
  'click',
  (event) => {
    const button = event.target instanceof Element ? event.target.closest(quickAddSelector) : null;
    if (!(button instanceof HTMLButtonElement) || !button.hasAttribute('data-in-cart')) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    removeFromCart(button);
  },
  { capture: true }
);

const elements = {
  'jf-header': JfHeader,
  'jf-dialog': JfDialog,
  'jf-share': JfShare,
  'jf-reveal': JfReveal,
  'jf-autoplay-video': JfAutoplayVideo,
  'jf-slideshow': JfSlideshow,
  'jf-carousel': JfCarousel,
  'jf-reel-strip': JfReelStrip,
};

for (const [name, element] of Object.entries(elements)) {
  if (!customElements.get(name)) customElements.define(name, element);
}
